import React, { useEffect } from 'react';

const CONTACT_EMAIL = 'itgroup@haldin-natural.com';

// Pakai <div>, bukan <section>: ada style global `section` (padding 48px) dari library
const Section = ({ title, children }) => (
  <div className="mt-8">
    <h2 className="text-2xl font-semibold text-neutral-900 mb-3">{title}</h2>
    <div className="space-y-3">{children}</div>
  </div>
);

const List = ({ children }) => (
  <ul className="list-disc pl-6 space-y-2">{children}</ul>
);

const Item = ({ label, children }) => (
  <li>
    {label && <strong className="font-semibold text-neutral-900">{label} </strong>}
    {children}
  </li>
);

const Email = () => (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-blue-600 underline break-all">
    {CONTACT_EMAIL}
  </a>
);

function PrivacyPolicy () {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'POSTalasi Privacy Policy';
    return () => {
      document.title = prevTitle;
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-16 text-neutral-700 leading-relaxed">
        <h1 className="text-3xl sm:text-4xl font-bold text-neutral-900">
          POSTalasi Privacy Policy
        </h1>

        <p className="mt-6">
          This privacy policy applies to the POSTalasi app (the &quot;Application&quot;) for mobile
          devices, created by Haldin Group (the &quot;Service Provider&quot;) as a free service. It
          explains what data the Application collects, why, who it is shared with, and how you can
          control it.
        </p>

        <Section title="Information We Collect">
          <h3 className="font-semibold text-neutral-900">Information you provide</h3>
          <List>
            <Item label="Account data:">
              name, phone number and/or email address, used to create your account and verify it
              with a one-time password (OTP).
            </Item>
            <Item label="Profile data:">
              information you add to your profile, including a profile photo.
            </Item>
            <Item label="Messages and content:">
              chat messages and photos you send to other users through the Application.
            </Item>
            <Item label="Photos and images:">
              images you choose from your device gallery or take with your camera (see &quot;Photos
              and Camera&quot; below).
            </Item>
          </List>
          <h3 className="font-semibold text-neutral-900">Information collected automatically</h3>
          <List>
            <Item>Your device&apos;s IP address and operating system.</Item>
            <Item>
              The pages of the Application you visit, the time and date of your visit, and the time
              spent in the Application.
            </Item>
            <Item>A device token used to send you push notifications.</Item>
          </List>
          <p>The Application does not collect precise location information from your device.</p>
        </Section>

        <Section title="Photos and Camera">
          <p>
            POSTalasi collects and uploads photos from your device gallery or camera to our servers
            to enable profile photos and sending images in chat, only when you choose or take a
            photo in those features.
          </p>
          <List>
            <Item label="When we access photos:">
              only after you tap an upload or camera button and accept the in-app disclosure, and
              then grant the Android permission. The Application never scans or uploads your gallery
              in the background.
            </Item>
            <Item label="How photos are used:">
              to display your profile photo and to deliver images you send in chat. Photos are not
              used for advertising.
            </Item>
            <Item label="Who can see them:">
              your profile photo is visible to other POSTalasi users; chat images are visible to the
              users in that conversation.
            </Item>
            <Item label="Storage:">
              uploaded photos are transmitted over an encrypted connection (HTTPS) and stored on
              servers operated by or for the Service Provider.
            </Item>
            <Item label="Your choice:">
              you can decline the disclosure or deny the permission and continue using the rest of
              the Application. You can withdraw camera or photo permission at any time in your
              device settings, and ask us to delete uploaded photos (see &quot;Data Retention and
              Deletion&quot;).
            </Item>
          </List>
        </Section>

        <Section title="How We Use Information">
          <List>
            <Item>To create and verify your account (OTP) and keep it secure.</Item>
            <Item>To provide chat, calling, profile and photo features.</Item>
            <Item>
              To send push notifications about messages, calls and important account notices.
            </Item>
            <Item>To maintain, troubleshoot and improve the Application.</Item>
            <Item>
              To contact you with important information and required notices. We send marketing
              messages only if you agree to receive them.
            </Item>
          </List>
        </Section>

        <Section title="Sharing of Information">
          <p>We do not sell your personal data. We share data only in these cases:</p>
          <List>
            <Item label="With other users,">
              as needed for the features you use (your profile, the messages and photos you send).
            </Item>
            <Item label="With service providers">
              that host our servers, deliver OTP messages or send push notifications on our behalf.
              They may use the data only to provide those services.
            </Item>
            <Item label="As required by law,">
              such as to comply with a subpoena or a government request, or when we believe in good
              faith that disclosure is needed to protect our rights, your safety or the safety of
              others, or to investigate fraud.
            </Item>
          </List>
          <p>
            Only aggregated, anonymized data is shared with external services to help us improve
            the Application.
          </p>
        </Section>

        <Section title="Data Retention and Deletion">
          <p>
            We keep your account data, messages and photos for as long as your account is active.
            You can ask us to delete your account and the data you provided, including uploaded
            photos, by emailing <Email />. We will process the request within 30 days, except for
            data we must keep by law.
          </p>
          <p>
            You can stop all future data collection by uninstalling the Application. Uninstalling
            does not delete data already stored on our servers; send a deletion request for that.
          </p>
        </Section>

        <Section title="Children">
          <p>
            The Application is not directed at children under 13, and we do not knowingly collect
            their personal data. You must be at least 16 years old, or the age required in your
            country, to consent to the processing of your personal data; in some countries a parent
            or guardian may consent on your behalf. If you believe a child has provided personal
            data, contact <Email /> and we will delete it.
          </p>
        </Section>

        <Section title="Security">
          <p>
            We use physical, electronic and procedural safeguards to protect your data, including
            encrypted connections (HTTPS) for data sent between the Application and our servers. No
            method of transmission or storage is completely secure.
          </p>
        </Section>

        <Section title="Changes to This Policy">
          <p>
            We may update this policy from time to time. We will post the new version on this page
            and update the effective date below. If a change significantly affects how we use your
            data, we will also notify you in the Application.
          </p>
          <p>This privacy policy is effective as of 2026-10-08.</p>
        </Section>

        <Section title="Contact Us">
          <p>
            For questions about this policy or your data, contact Haldin Group at <Email />.
          </p>
        </Section>
      </main>
    </div>
  );
}

export default PrivacyPolicy;
